'use client';

import { c as _c } from "react/compiler-runtime";
import { cjk } from '@streamdown/cjk';
import { code } from '@streamdown/code';
import { math } from '@streamdown/math';
import { createMermaidPlugin } from '@streamdown/mermaid';
import type { ComponentProps } from 'react';
import { memo } from 'react';
import { Streamdown } from 'streamdown';
import { handleMarkdownCopy } from './chat-markdown-copy';
import { ClickableInlineCode } from './clickable-inline-code';
import { cn } from '@/lib/utils';
type ChatTextMarkdownProps = {
  children: string;
  className?: string;
};
const CHAT_TEXT_STREAMDOWN_CLASSNAME = cn(
// text-zinc-300 (not text-foreground) so chat copy matches the muted body
// text used across the rest of the PR view (thread cards, sidebar, etc).
'min-w-0 max-w-full text-[13px] leading-relaxed text-zinc-300', '[&_a]:break-all', '[&_blockquote]:my-3', '[&_code]:text-[0.9em]',
// Streamdown's default code-block wrapper (my-4 / gap-2 / rounded-xl / p-2) is tuned
// for wide ChatGPT-style canvases. In the narrow PR sidebar that becomes way too much
// chrome around short snippets, so we tighten the wrapper here. Specificity beats the
// base class because `&_[data-streamdown="code-block"]` is a descendant selector.
'[&_[data-streamdown="code-block"]]:my-2', '[&_[data-streamdown="code-block"]]:gap-1', '[&_[data-streamdown="code-block"]]:rounded-md', '[&_[data-streamdown="code-block"]]:p-1',
// Hide the per-block header (language label + copy/download buttons). The chat surface
// is too narrow for it to be useful and the language tag adds noise above every snippet.
'[&_[data-streamdown="code-block-header"]]:hidden',
// For the narrow chat sidebar, prefer wrapped fenced code so long lines stay visible without
// forcing a horizontal scroll gesture. This is intentionally scoped to chat streamdown blocks.
'[&_[data-streamdown="code-block-body"]]:overflow-x-hidden', '[&_[data-streamdown="code-block-body"]]:overflow-y-hidden', '[&_[data-streamdown="code-block-body"]>pre]:min-w-0', '[&_[data-streamdown="code-block-body"]>pre]:max-w-full', '[&_[data-streamdown="code-block-body"]>pre]:whitespace-pre-wrap', '[&_[data-streamdown="code-block-body"]>pre]:break-words',
// Kill the browser's user-agent `pre { margin: 1em 0 }`. Without this the code body
// ends up with ~16px more vertical padding than horizontal padding, making the box
// visibly taller than wide on short snippets.
'[&_[data-streamdown="code-block-body"]>pre]:m-0',
// Hide the trailing empty line span that Streamdown emits when the source code ends
// with a newline (which most LLM-generated fenced blocks do). Empty lines render as
// `<span class="block">\n</span>` with only a text child, while non-empty lines wrap
// each token in an inner `<span>` — so `:not(:has(> span))` precisely targets the
// empty trailing line and not legitimate single-token lines. This is the source of
// the visual asymmetry where bottom padding appears larger than top padding.
'[&_[data-streamdown="code-block-body"]>pre>code>span:last-child:not(:has(>span))]:hidden', '[&_details]:my-3', '[&_h1]:mb-3 [&_h1]:mt-6 [&_h1]:text-[13px] [&_h1]:font-semibold', '[&_h2]:mb-2 [&_h2]:mt-5 [&_h2]:text-[13px] [&_h2]:font-semibold', '[&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:text-[13px] [&_h3]:font-semibold', '[&_h4]:mb-1.5 [&_h4]:mt-3 [&_h4]:text-[13px] [&_h4]:font-medium', '[&_h5]:mb-1.5 [&_h5]:mt-3 [&_h5]:text-[13px] [&_h5]:font-medium', '[&_h6]:mb-1.5 [&_h6]:mt-3 [&_h6]:text-[13px] [&_h6]:font-medium', '[&_hr]:my-4', '[&_img]:max-w-full [&_img]:rounded-lg', '[&_li]:text-[13px] [&_li]:leading-relaxed', '[&_[data-streamdown="list-item"]]:pt-0', '[&_[data-streamdown="list-item"]>*]:align-top', '[&_[data-streamdown="ordered-list"]]:list-outside', '[&_[data-streamdown="ordered-list"]_li]:pl-1', '[&_[data-streamdown="unordered-list"]]:list-outside', '[&_[data-streamdown="unordered-list"]_li]:pl-1', '[&_ol]:my-2 [&_ol]:ml-4', '[&_p]:text-[13px] [&_p]:leading-relaxed', '[&_pre]:text-[13px]', '[&_summary]:cursor-pointer', '[&_table]:text-[13px]', '[&_ul]:my-2 [&_ul]:ml-4');

// Streamdown's preconfigured Mermaid plugin uses Mermaid's light "default"
// theme. Its transparent canvas sits on Cubic's dark chat surface, making
// sequence labels and messages render as nearly invisible black text.
const mermaid = createMermaidPlugin({
  config: {
    theme: 'dark'
  }
});
const streamdownPlugins = {
  cjk,
  code,
  math,
  mermaid
};
const streamdownControls = {
  code: {
    copy: true,
    download: false
  }
} as const;
const streamdownComponents = {
  inlineCode: ClickableInlineCode
};
const NonMemoizedChatTextMarkdown = t0 => {
  const $ = _c(10);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = event => handleMarkdownCopy(event, children);
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== className) {
    t2 = cn(CHAT_TEXT_STREAMDOWN_CLASSNAME, className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t2) {
    t3 = <Streamdown className={t2} components={streamdownComponents} controls={streamdownControls} mode="streaming" plugins={streamdownPlugins as ComponentProps<typeof Streamdown>['plugins']}>{children}</Streamdown>;
    $[4] = children;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <div className="min-w-0 max-w-full" onCopy={t1}>{t3}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export const ChatTextMarkdown = memo(NonMemoizedChatTextMarkdown);